import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callAPI } from '../../../utils/apiUtils';
import { apiUrls } from '../../../utils/apiUrls';
import { useDispatch } from 'react-redux';
import { setAuthUser, updateAuthUser } from '../../../redux/slices/authSlice';
import { setUser } from '../../../redux/slices/profileSlice';

const fetchUserProfile = async () => {
    const response = await callAPI(apiUrls.getProfile, {}, 'GET', {});
    if (response?.data?.success) {
        const userData = response?.data?.data;
        const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
         const mergedUser = {
            ...currentUser,
            ...userData,
            id: userData?._id
        };
        localStorage.setItem('userData', JSON.stringify(mergedUser));
        return userData;
    }
    throw new Error(response?.data?.message || 'Failed to fetch profile');
};

const updateUserProfileAPI = async (profileData) => {
    const response = await callAPI(apiUrls.editProfile, {}, 'PATCH', profileData);
    if (response?.data?.success) {
        const updatedUser = response?.data?.data;
        const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
         const mergedUser = {
            ...currentUser,
            ...updatedUser,
            id: updatedUser?._id
        };
        localStorage.setItem('userData', JSON.stringify(mergedUser));
        return {
            user: mergedUser,
            message: response?.data?.message || 'Profile updated successfully'
        };
    }
    throw new Error(response?.data?.message || 'Profile update failed');
};

const changePasswordAPI = async (passwordData) => {
    const response = await callAPI(apiUrls.changePassword, {}, 'PATCH', passwordData);
    if (response?.data?.success) {
        return response?.data?.message || 'Password changed successfully';
    }
    throw new Error(response?.data?.message || 'Password change failed');
};

const deleteAccountAPI = async () => {
    const response = await callAPI(apiUrls.deleteAccount, {}, 'DELETE', {});
    if (response?.data?.success) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userData');
        return response?.data?.message || 'Account deleted successfully';
    }
    throw new Error(response?.data?.message || 'Account deletion failed');
};

export const useUserProfile = (options = {}) => {
    const dispatch = useDispatch();
    return useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => {
            const data = await fetchUserProfile();
            if (data) {
                dispatch(setAuthUser(data));
                dispatch(setUser(data));
            }
            return data;
        },
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        ...options,
    });
};

export const useUpdateUserProfile = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: updateUserProfileAPI,
        onSuccess: (data) => {
            if (data?.user) {
                dispatch(setAuthUser(data.user));
                dispatch(setUser(data.user));
                dispatch(updateAuthUser(data.user));
            }
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            queryClient.invalidateQueries({ queryKey: ['carrierProfile'] });
        },
    });
};

export const useChangePassword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: changePasswordAPI,
        onSuccess: () => { },
    });
};

export const useDeleteAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteAccountAPI,
        onSuccess: () => {
            queryClient.clear();
        },
    });
};

let invalidateTimeout = null;

export const useInvalidateProfileQueries = () => {
    const queryClient = useQueryClient();

    const invalidate = () => {
        if (invalidateTimeout) {
            clearTimeout(invalidateTimeout);
        }
        invalidateTimeout = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            queryClient.invalidateQueries({ queryKey: ['carrierProfile'] });
            invalidateTimeout = null;
        }, 100);
    };

    return invalidate;
};